"use client";

import Image from "next/image";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { useTransition } from "react";
import { ArrowRight, ChevronLeft, ChevronRight, MapPin } from "lucide-react";
import type { WorldCupMatch } from "@/lib/world-cup-data";
import { getResponsiveTeamLabel } from "@/lib/responsive-team-label";
import { cn, initialsFromName } from "@/lib/utils";
import { getHungarianWorldCupTeamName } from "@/lib/world-cup-team-names";
import { openWorldCupMatchThreadAction } from "@/lib/forum-actions";

const desktopMatchesPerPage = 3;
const hungarianTimeZone = "Europe/Budapest";

function getResponsiveMatchesPerPage() {
  if (typeof window === "undefined") return desktopMatchesPerPage;
  if (window.matchMedia("(min-width: 1024px)").matches) return 3;
  if (window.matchMedia("(min-width: 640px)").matches) return 2;
  return 1;
}

function formatMatchPhase(match: WorldCupMatch) {
  if (match.group) return match.group.replace("_", " ");
  if (match.stage) return match.stage.replace(/_/g, " ");
  if (match.matchday) return `${match.matchday}. forduló`;
  return match.competitionName ?? match.status;
}

function matchTime(match: WorldCupMatch) {
  return new Intl.DateTimeFormat("hu-HU", {
    timeZone: hungarianTimeZone,
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(match.utcDate));
}

function matchDate(match: WorldCupMatch) {
  return new Intl.DateTimeFormat("hu-HU", {
    timeZone: hungarianTimeZone,
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(match.utcDate));
}

function matchCenter(match: WorldCupMatch) {
  if (match.homeScore !== null && match.awayScore !== null) {
    return `${match.homeScore} - ${match.awayScore}`;
  }
  return matchTime(match);
}

function isLive(status: string) {
  return ["IN_PLAY", "LIVE", "PAUSED"].includes(status);
}

const matchThreadButtonLabel = "Fórum és vélemények";

function teamLabels(team: NonNullable<WorldCupMatch["homeTeam"]>) {
  const teamName = getHungarianWorldCupTeamName({ name: team.name, code: team.code }) || team.name;

  return {
    desktop: getResponsiveTeamLabel({ name: teamName, shortName: team.shortName, tla: team.tla ?? team.code }, "desktop"),
    tablet: getResponsiveTeamLabel({ name: teamName, shortName: team.shortName, tla: team.tla ?? team.code }, "tablet"),
    mobile: getResponsiveTeamLabel({ name: teamName, shortName: team.shortName, tla: team.tla ?? team.code }, "mobile"),
  };
}

function TeamSide({
  team,
  align = "left",
}: {
  team: WorldCupMatch["homeTeam"];
  align?: "left" | "right";
}) {
  if (!team) {
    return (
      <div className={cn("min-w-0", align === "right" ? "text-right" : "text-left")}>
        <span className="text-xs font-black uppercase text-[#8B8175]">TBD</span>
      </div>
    );
  }
  const teamName = getHungarianWorldCupTeamName({ name: team.name, code: team.code }) || team.name;
  const labels = teamLabels(team);

  return (
    <div
      className={cn(
        "flex min-w-0 items-center gap-2",
        align === "right" ? "flex-row-reverse text-right" : "text-left",
      )}
    >
      {team.crest ? (
        <span className="relative h-9 w-9 shrink-0 overflow-hidden rounded-full border border-[#E5DDD0] bg-white">
          <Image src={team.crest} alt="" fill sizes="36px" className="object-contain p-1" />
        </span>
      ) : (
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#F7F5EF] text-lg">
          {team.flag ?? initialsFromName(teamName).slice(0, 2)}
        </span>
      )}
      <span className="min-w-0">
        <span className="block shrink-0 text-sm font-black leading-4 text-[#111111] sm:hidden">{labels.mobile}</span>
        <span className="hidden min-w-0 truncate text-sm font-black leading-4 text-[#111111] sm:block lg:hidden">{labels.tablet}</span>
        <span className="hidden min-w-0 truncate text-sm font-black leading-4 text-[#111111] lg:block">{labels.desktop}</span>
        <span className="block truncate text-[10px] font-bold uppercase tracking-wide text-[#8B8175]">{labels.desktop}</span>
      </span>
    </div>
  );
}

export function WorldCupNextMatches({ matches }: { matches: WorldCupMatch[] }) {
  const router = useRouter();
  const [pageIndex, setPageIndex] = useState(0);
  const [matchesPerPage, setMatchesPerPage] = useState(desktopMatchesPerPage);
  const [pendingMatchId, setPendingMatchId] = useState<string | null>(null);
  const [threadError, setThreadError] = useState<string | null>(null);
  const [isThreadPending, startThreadTransition] = useTransition();
  const pageCount = Math.max(1, Math.ceil(matches.length / matchesPerPage));
  const activePageIndex = Math.min(pageIndex, pageCount - 1);
  const visibleMatches = useMemo(
    () => matches.slice(activePageIndex * matchesPerPage, activePageIndex * matchesPerPage + matchesPerPage),
    [activePageIndex, matches, matchesPerPage],
  );

  useEffect(() => {
    function updateMatchesPerPage() {
      setMatchesPerPage(getResponsiveMatchesPerPage());
    }

    updateMatchesPerPage();
    window.addEventListener("resize", updateMatchesPerPage);
    return () => window.removeEventListener("resize", updateMatchesPerPage);
  }, []);

  function previousPage() {
    setPageIndex(Math.max(0, activePageIndex - 1));
  }

  function nextPage() {
    setPageIndex(Math.min(pageCount - 1, activePageIndex + 1));
  }

  function openMatchThread(matchId: string) {
    setThreadError(null);
    setPendingMatchId(matchId);
    startThreadTransition(async () => {
      const result = await openWorldCupMatchThreadAction(matchId);
      setPendingMatchId(null);
      if (!result.ok) {
        setThreadError(result.message);
        return;
      }
      router.push(result.data?.href ?? "/forum");
    });
  }

  return (
    <section className="mt-12">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3 border-b-2 border-[#111111] pb-3">
        <h2 className="font-display text-4xl font-bold leading-none text-[#111111]">Következő mérkőzések</h2>
        {matches.length ? (
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={previousPage}
              disabled={activePageIndex === 0}
              className="grid h-8 w-8 place-items-center rounded-full border border-[#D8D0C4] bg-white text-[#111111] transition hover:border-[#111111] disabled:cursor-not-allowed disabled:opacity-35"
              aria-label="Előző mérkőzések"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </button>
            <span className="min-w-10 text-center text-xs font-black tabular-nums text-[#111111]">
              {activePageIndex + 1} / {pageCount}
            </span>
            <button
              type="button"
              onClick={nextPage}
              disabled={activePageIndex === pageCount - 1}
              className="grid h-8 w-8 place-items-center rounded-full border border-[#D8D0C4] bg-white text-[#111111] transition hover:border-[#111111] disabled:cursor-not-allowed disabled:opacity-35"
              aria-label="Következő mérkőzések"
            >
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        ) : null}
      </div>
      {visibleMatches.length ? (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {visibleMatches.map((match) => (
              <article
                key={match.id}
                className={cn(
                  "rounded-md border bg-white p-3 shadow-sm",
                  isLive(match.status) ? "border-[#D8973C] shadow-[#D8973C]/15" : "border-[#E5DDD0]",
                )}
              >
                <div className="text-center text-[10px] font-black uppercase tracking-[0.2em] text-[#A4243B]">
                  {formatMatchPhase(match)}
                </div>
                <div className="mt-3 grid grid-cols-[minmax(0,1fr)_76px_minmax(0,1fr)] items-center gap-2">
                  <TeamSide team={match.homeTeam} />
                  <div className="text-center">
                    <div
                      className={cn(
                        "rounded-sm px-2 py-2 text-lg font-black tabular-nums",
                        isLive(match.status) ? "bg-[#D8973C] text-[#111111]" : "bg-[#111111] text-white",
                      )}
                    >
                      {matchCenter(match)}
                    </div>
                    <div className="mt-1 text-[9px] font-black uppercase tracking-[0.16em] text-[#8B8175]">
                      {matchDate(match)}
                    </div>
                  </div>
                  <TeamSide team={match.awayTeam} align="right" />
                </div>
                <div className="mt-3 min-h-9 text-center text-[11px] font-semibold leading-4 text-[#6B6B6B]">
                  <div>{match.matchday ? `${match.matchday}. forduló` : match.stage?.replace(/_/g, " ") ?? match.status}</div>
                  {match.venue ? (
                    <div className="mt-1 inline-flex max-w-full items-center justify-center gap-1.5">
                      <MapPin size={13} />
                      <span className="truncate">{match.venue}</span>
                    </div>
                  ) : null}
                </div>
                <button
                  type="button"
                  disabled={isThreadPending && pendingMatchId === match.id}
                  onClick={() => openMatchThread(match.id)}
                  className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-sm border border-[#111111] px-3 py-2 text-[11px] font-black uppercase tracking-[0.14em] text-[#111111] transition hover:bg-[#111111] hover:text-white disabled:cursor-wait disabled:opacity-60"
                >
                  {isThreadPending && pendingMatchId === match.id ? "Megnyitás..." : matchThreadButtonLabel}
                  <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </article>
            ))}
          </div>
          {threadError ? (
            <p className="mt-3 rounded-md border border-[#A4243B]/20 bg-[#A4243B]/5 px-3 py-2 text-sm font-semibold text-[#A4243B]" role="alert">
              {threadError}
            </p>
          ) : null}
        </>
      ) : (
        <div className="rounded-md border border-dashed border-[#D8C7AE] bg-[#F7F5EF] p-6 text-sm font-semibold text-[#6B6B6B]">
          A következő mérkőzések hamarosan elérhetők.
        </div>
      )}
    </section>
  );
}
